import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { v4 } from 'uuid';

import { Table, Form } from 'react-bootstrap'; // Removed Button as it's not used here
import {
  useTable,
  usePagination,
  useSortBy,
  useFilters,
  useRowState,
} from 'react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import useWindowSize from '../../hooks/useWindowSize';
import DataTablePagination from './DataTablePagination';

export default function DataTable({
  columns,
  items,
  updateMyData,
  nameAsc,
  searchLabel,
  cellClassname,
  defaultSort,
  hiddenColumns,
  hideHeader,
  hideToolbar,
  striped,
  defaultPageSize,
  showactions,
  customActions,
  defaultPageIndex,
  // New props for server-side pagination
  fetchData, // ({ pageIndex, pageSize }) => void
  pageCountFromServer, // number (total pages from server)
  // loading, // Prop to indicate loading state, not directly used for a spinner inside DataTable
}) {
  const data = useMemo(() => items, [items]);

  const {
    getTableProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount, // This will be pageCountFromServer due to manualPagination
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
    setHiddenColumns,
  } = useTable(
    {
      columns,
      data,
      initialState: {
        hiddenColumns: hiddenColumns || [],
        pageIndex: defaultPageIndex || 0,
        pageSize: defaultPageSize || 10, // Default to 10 if not provided
        sortBy: [
          {
            id: defaultSort || 'name',
            desc: !!nameAsc,
          },
        ],
      },
      manualPagination: true,
      pageCount: pageCountFromServer,
      autoResetPage: false, // Important for server-side pagination
      updateMyData,
      disableSortRemove: true,
      autoResetRowState: false,
    },
    useFilters,
    useSortBy,
    usePagination,
    useRowState
  );

  useEffect(() => {
    if (typeof fetchData === 'function') {
      fetchData({ pageIndex, pageSize });
    }
  }, [fetchData, pageIndex, pageSize]);

  const perPageOptions = [
    { value: 5, label: 5 }, { value: 10, label: 10 },
    { value: 15, label: 15 }, { value: 20, label: 20 },
    { value: 25, label: 25 },
  ];

  const size = useWindowSize();

  useEffect(() => {
    if (size.width < 991) {
      searchLabel === 'categories'
        ? setHiddenColumns(['plant_type.name', 'description'])
        : setHiddenColumns(['slug', 'plant_category.name']);
    } else {
      setHiddenColumns([]);
    }
  }, [size.width, searchLabel, setHiddenColumns, columns]); // Added columns to dependency array as setHiddenColumns might depend on available columns

  return (
    <>
      <div className={`dataTable-top ${hideToolbar ? 'd-none' : ''}`}>
        {showactions === true && (
          <span className="me-2">
            <Form.Select size="sm" className="d-inline w-auto me-1">
              <option>Remover</option> {/* This seems like a placeholder action */}
            </Form.Select>
            {/* Removed Apply button as its functionality wasn't clear with server-side pagination */}
          </span>
        )}
        {customActions}
        <div className="d-inline-block">
          <Form.Select
            className="d-inline w-auto me-1"
            onChange={(e) => setPageSize(Number(e.target.value))}
            value={pageSize}
            aria-label="Registros por página"
          >
            {perPageOptions.map((option) => (
              <option key={option.label} value={option.value}>{option.label}</option>
            ))}
          </Form.Select>
        </div>
        <span className="text-xs">Registros por página</span>
      </div>
      <Table hover {...getTableProps()} className="align-middle mb-0" striped={striped} responsive>
        <thead className={hideHeader ? 'd-none' : ''}>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={v4()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps(column.getSortByToggleProps())} className="py-4" key={v4()}>
                  <span className="d-flex align-items-center text-transform-none ">
                    {column.render('Header')}
                    {!column.disableSortBy && (
                      <span className="d-grid ms-auto">
                        <FontAwesomeIcon icon={faCaretUp} className={`ms-1 ${!column.isSortedDesc ? 'opacity-6' : ''}`} size="sm" />
                        <FontAwesomeIcon icon={faCaretDown} className={`ms-1 ${column.isSorted && !column.isSortedDesc ? '' : 'opacity-6'}`} size="sm" />
                      </span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="position-relative border-top-0">
          {page.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()} key={v4()}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()} className={cellClassname || ''} key={v4()}>
                    {cell.render('Cell')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </Table>
      <div className="dataTable-bottom align-items-center d-flex">
        <div className="flex-shrink-0 mb-2 mb-md-0 me-auto">
          Visualizando página {pageIndex + 1} de {pageCount} {/* pageCount from useTable is correct */}
        </div>
        {pageOptions.length > 1 && (
          <DataTablePagination
            canPreviousPage={canPreviousPage}
            canNextPage={canNextPage}
            gotoPage={gotoPage}
            nextPage={nextPage}
            previousPage={previousPage}
            pageIndex={pageIndex}
            pageOptions={pageOptions}
            viewportWidth={size.width || 500}
          />
        )}
      </div>
    </>
  );
}

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      Header: PropTypes.string.isRequired,
      accessor: PropTypes.string.isRequired,
      noSearch: PropTypes.bool,
    })
  ).isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  updateMyData: PropTypes.func,
  nameAsc: PropTypes.string,
  searchLabel: PropTypes.string,
  cellClassname: PropTypes.string,
  defaultSort: PropTypes.string,
  hiddenColumns: PropTypes.arrayOf(PropTypes.string),
  hideHeader: PropTypes.bool,
  hideToolbar: PropTypes.bool,
  striped: PropTypes.bool,
  defaultPageSize: PropTypes.number,
  showactions: PropTypes.bool,
  customActions: PropTypes.node,
  defaultPageIndex: PropTypes.number,
  fetchData: PropTypes.func.isRequired,
  pageCountFromServer: PropTypes.number.isRequired,
  // loading: PropTypes.bool, // loading prop is not strictly required by DataTable itself
};

DataTable.defaultProps = {
  updateMyData: null,
  nameAsc: 'desc',
  searchLabel: '',
  cellClassname: '',
  defaultSort: '',
  hiddenColumns: [],
  hideHeader: false,
  hideToolbar: false,
  striped: false,
  defaultPageSize: 10,
  showactions: false,
  customActions: null,
  defaultPageIndex: 0,
  // fetchData and pageCountFromServer are required, so no defaults.
  // loading: false,
};

